/**
 * Post-authentication redirect (issue #452).
 *
 * The "read returnTo from the query string, then navigate" block was pasted
 * three times across login.ts and register.ts, inside anonymous callbacks in
 * side-effect-only entry modules — unvalidated and untestable. Centralising it
 * here means the origin check has one call site per behaviour instead of three,
 * and the logic can actually be unit-tested.
 *
 * Usage:
 *   redirectAfterAuth();        // login.ts  — defaults to /
 *   redirectAfterAuth('/map');  // register.ts — defaults to /map
 */
import { safeReturnTo } from '../../shared/utils/safeReturnTo.js';

/**
 * Resolve the current URL's `returnTo` to a safe same-origin path and navigate.
 *
 * @param defaultPath - Where to go when `returnTo` is absent or unsafe
 */
export function redirectAfterAuth(defaultPath = '/'): void {
  window.location.href = resolveReturnTo(defaultPath);
}

/**
 * The resolution half of {@link redirectAfterAuth}, without the navigation —
 * exported so it can be asserted on directly in tests.
 *
 * @param defaultPath - Where to go when `returnTo` is absent or unsafe
 * @returns A safe same-origin path
 */
export function resolveReturnTo(defaultPath = '/'): string {
  const params = new URLSearchParams(window.location.search);
  return safeReturnTo(params.get('returnTo'), window.location.origin, defaultPath);
}
